import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Circle, ListTodo, CornerDownRight, Calendar } from "lucide-react";
import { PRIORITY, Dot, fmtDate } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { assigneeIds } from "@/components/Assignees";

export function MySubtaskList({ subs, testid = "my-subtasks" }) {
  const navigate = useNavigate();
  if (!subs.length) return null;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {subs.map((s) => (
        <button key={s.id} onClick={() => navigate(`/boards/${s.board_id}?task=${s.task_id}`)} data-testid={`my-subtask-${s.id}`}
          className="w-full text-left bg-white border border-slate-200 rounded-lg px-3 py-2.5 hover:border-indigo-300 transition-colors flex items-center gap-2.5">
          {s.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className={`text-sm truncate ${s.done ? "line-through text-slate-400" : "text-slate-800"}`}>{s.title}</p>
            <p className="text-[11px] text-slate-400 truncate inline-flex items-center gap-1"><CornerDownRight className="w-3 h-3" />{s.task_title} · {s.board_name}</p>
          </div>
          {s.due_date && (
            <span className={`text-[11px] inline-flex items-center gap-1 shrink-0 ${s.overdue ? "text-red-600 font-semibold" : "text-slate-500"}`} data-testid={`my-subtask-due-${s.id}`}>
              <Calendar className="w-3 h-3" />{s.overdue ? "Overdue · " : ""}{fmtDate(s.due_date + "T00:00:00")}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export function useMySubtasks() {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/subtasks/mine").then((r) => setSubs(r.data)).catch(() => {}); }, []);
  return subs;
}

export default function MyWorkCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const subs = useMySubtasks();
  useEffect(() => { api.get("/tasks", { params: { assignee_id: user.id } }).then((r) => setTasks(r.data.filter((t) => t.category !== "completed"))); }, [user.id]);
  const openSubs = subs.filter((s) => !s.done);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="my-work-card">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><ListTodo className="w-4 h-4" /></span>
        <h3 className="font-display font-semibold text-slate-900">Assigned to me ({tasks.length + openSubs.length})</h3>
      </div>
      <div className="space-y-1.5 max-h-[320px] overflow-y-auto scroll-thin">
        {tasks.map((t) => (
          <button key={t.id} onClick={() => navigate(`/boards/${t.board_id}?task=${t.id}`)} data-testid={`my-work-task-${t.id}`}
            className="w-full text-left rounded-lg px-2 py-2 hover:bg-slate-50 flex items-center gap-2.5">
            <Dot className={PRIORITY[t.priority]?.dot} />
            <span className="text-sm text-slate-800 truncate flex-1">{t.title}</span>
            {assigneeIds(t).length > 1 && <span className="text-[10px] text-slate-400">+{assigneeIds(t).length - 1}</span>}
            <span className="text-[11px] text-slate-400 shrink-0">{t.stage_name}</span>
          </button>
        ))}
        {openSubs.length > 0 && <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 pt-2 px-2">Subtasks</p>}
        <MySubtaskList subs={openSubs} testid="my-work-subtasks" />
        {!tasks.length && !openSubs.length && <p className="text-sm text-slate-400">Nothing assigned to you right now.</p>}
      </div>
    </div>
  );
}
